import * as types from '../mutation-types';
import { createActionHelpers } from 'vuex-loading';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loader',
});

const OPENED = 0;
const ORDERED = 1;
const RECEIVED = 2;

const state = {
    purchaseOrder: {
        id: null,
        status: 0,
        order_no: null,
        outlet: {
            name: null,
            id: null,
        },
        remarks: null,
        outlet_id: null,
        supplier: {
            name: null,
            id: null,
            contact_email: null,
        },
        supplier_id: null,
        delivery_address: null,
        order_date: null,
        delivery_date: null,
        stocks: [],
    },
};

const getters = {
    purchaseOrder: state => state.purchaseOrder,
    supplier: state => state.purchaseOrder.supplier,
    hasFinalized: state => state.purchaseOrder.status == RECEIVED,
    stocks: state => state.purchaseOrder.stocks,
    outlet: state => state.purchaseOrder.outlet,
    totalPrice: (state, getters) => {
        return getters.stocks
            .map(stock => {
                const price = parseFloat(stock.price);
                const quantity = parseFloat(stock.order_quantity);
                const validPrice = !_.isNaN(price) && !_.isNaN(quantity);
                const totalPrice = validPrice ? price * quantity : 0;

                return parseFloat(totalPrice.toFixed(2));
            })
            .reduce((a, c) => a + c, 0);
    },
    completionStatus(state) {
        if (state.purchaseOrder.status == RECEIVED) return 'Received';
        else if (state.purchaseOrder.status == ORDERED) return 'Ordered';
        return 'Open';
    },
    findStockIndex: (state, getters) => stock_id => {
        return getters.stocks.findIndex(s => s.stock_id == stock_id);
    },
};

const mutations = {
    [types.SET_PURCHASE_ORDER](state, purchaseOrder) {
        state.purchaseOrder = purchaseOrder;
    },

    [types.SET_PURCHASE_ORDER_ATTR](state, { key, value }) {
        state.purchaseOrder[key] = value;
    },

    [types.ADD_PURCHASE_STOCK](state, stock) {
        state.purchaseOrder.stocks.push(stock);
    },

    [types.REMOVE_PURCHASE_STOCK](state, { index }) {
        state.purchaseOrder.stocks.splice(index, 1);
    },

    [types.UPDATE_PURCHASE_STOCK](state, { index, key, value }) {
        state.purchaseOrder.stocks[index][key] = value;
    },
};

const actions = {
    setPurchaseOrder({ commit }, purchaseOrder) {
        commit(types.SET_PURCHASE_ORDER, purchaseOrder);
    },

    setPurchaseOrderAttr({ commit }, { key, value }) {
        commit(types.SET_PURCHASE_ORDER_ATTR, { key, value });
    },

    addPurchaseStock({ commit, getters }, stockInventory) {
        let price = null;

        // find supplier price if any
        const supplierStock = getters.supplier.stocks.find(
            s => s.id == stockInventory.stock_id,
        );

        if (!_.isNil(supplierStock)) {
            price = supplierStock.pivot.cost;
        } else {
            price = stockInventory.cost;
        }

        const stock = {
            stock_id: stockInventory.stock_id,
            stock: stockInventory,
            current_quantity: stockInventory.quantity,
            order_quantity: null,
            received_quantity: null,
            price,
        };

        commit(types.ADD_PURCHASE_STOCK, stock);
    },

    removePurchaseStock({ commit, getters }, stock_id) {
        const stockIndex = getters.findStockIndex(stock_id);

        if (stockIndex >= 0) {
            commit(types.REMOVE_PURCHASE_STOCK, stockIndex);
        }
    },

    setStockPurchaseAttr({ commit }, { key, value }) {
        commit(types.SET_STOCK_PURCHASE_ATTR, { key, value });
    },

    updatePurchaseStock({ commit, getters }, { stock_id, key, value }) {
        const index = getters.findStockIndex(stock_id);
        commit(types.UPDATE_PURCHASE_STOCK, { index, key, value });
    },

    async getPurchaseOrder({ commit, dispatch }, id) {
        const route = Laroute.route('purchase_order.show', { id });
        startLoading(dispatch, 'fetch-purchase-order');

        try {
            const { data } = await axios.get(route);
            commit(types.SET_PURCHASE_ORDER, data);
            endLoading(dispatch, 'fetch-purchase-order');

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-purchase-order');
            throw e;
        }
    },

    async createPurchaseOrder({ commit, dispatch }, po) {
        const route = Laroute.route('purchase_order.store');
        startLoading(dispatch, 'save-purchase-order');

        try {
            const { data } = await axios.post(route, po);
            commit(types.SET_PURCHASE_ORDER, data);
            endLoading(dispatch, 'save-purchase-order');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-purchase-order');
            throw e;
        }
    },

    async updatePurchaseOrder(
        { commit, getters, dispatch },
        { action = 'save', status = null } = {},
    ) {
        const id = getters.purchaseOrder.id;
        let route = 'purchase_order.update';
        const newStatus = status !== null ? { status } : {};

        startLoading(dispatch, `${action}-purchase-order`);

        if (status == ORDERED) {
            route = 'purchase_order.ordered';
        } else if (status == RECEIVED) {
            route = 'purchase_order.received';
        }

        try {
            const { data } = await axios.put(
                Laroute.route(route, { id }),
                Object.assign({}, getters.purchaseOrder, newStatus),
            );
            commit(types.SET_PURCHASE_ORDER, data);
            endLoading(dispatch, `${action}-purchase-order`);

            return data;
        } catch (e) {
            endLoading(dispatch, `${action}-purchase-order`);
            throw e;
        }
    },

    async sendMail(
        { dispatch, state },
        { subject, address, cc = [], message = null },
    ) {
        startLoading(dispatch, 'mail-purchase-order');

        const id = state.purchaseOrder.id;
        const route = Laroute.route('purchase_order.mail', { id });

        const options = {
            subject,
            address,
            cc,
            message,
        };

        try {
            const { data } = await axios.post(route, options);
            endLoading(dispatch, 'mail-purchase-order');

            return data;
        } catch (e) {
            endLoading(dispatch, 'mail-purchase-order');
            throw e;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/purchase-order.js